/**
 * MotivationAdminPage - Full management page for motivation settings
 * - Daily objective (amount + message)
 * - Employee of the month (photo, name, position, message)
 * - History of past employees
 * Admin only access
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
    Target, Award, Save, Upload, User, Trash2,
    Sparkles, Trophy, Star, Archive, History,
    AlertTriangle, CheckCircle, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const MotivationAdminPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const fileInputRef = useRef(null);

    // Redirect non-admin users
    useEffect(() => {
        if (user && user.role !== 'ADMIN') {
            navigate('/dashboard');
            toast.error('Accès réservé à l\'administrateur');
        }
    }, [user, navigate]);

    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        daily_objective: { amount: 0, message: '' },
        employee_of_month: { name: '', position: '', photo_url: '', message: '' }
    });
    const [history, setHistory] = useState([]);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedHistoryId, setSelectedHistoryId] = useState(null);

    // Form state
    const [objectiveAmount, setObjectiveAmount] = useState(0);
    const [objectiveMessage, setObjectiveMessage] = useState('');
    const [employeeName, setEmployeeName] = useState('');
    const [employeePosition, setEmployeePosition] = useState('');
    const [employeeMessage, setEmployeeMessage] = useState('');
    const [employeePhoto, setEmployeePhoto] = useState('');

    // Fetch data on mount
    useEffect(() => {
        fetchSettings();
        fetchHistory();
    }, []);

    const fetchSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                // Initialize form fields
                setObjectiveAmount(data.daily_objective?.amount || 0);
                setObjectiveMessage(data.daily_objective?.message || '');
                setEmployeeName(data.employee_of_month?.name || '');
                setEmployeePosition(data.employee_of_month?.position || '');
                setEmployeeMessage(data.employee_of_month?.message || '');
                setEmployeePhoto(data.employee_of_month?.photo_url || '');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setHistory(data.history || []);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const saveObjective = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/settings`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    daily_objective: {
                        amount: objectiveAmount,
                        message: objectiveMessage
                    }
                })
            });

            if (response.ok) {
                toast.success('Objectif mis à jour !');
                fetchSettings();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setSaving(false);
        }
    };

    const saveEmployee = async (archive = false) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/settings`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    employee_of_month: {
                        name: employeeName,
                        position: employeePosition,
                        photo_url: employeePhoto,
                        message: employeeMessage
                    },
                    archive_current_employee: archive
                })
            });

            if (response.ok) {
                toast.success('Employé du mois mis à jour !');
                fetchSettings();
                if (archive) {
                    fetchHistory();
                }
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setSaving(false);
            setArchiveDialogOpen(false);
        }
    };

    const handlePhotoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('La photo ne doit pas dépasser 2 Mo');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE}/api/motivation/upload-photo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                setEmployeePhoto(result.photo_url);
                toast.success('Photo téléchargée !');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors du téléchargement');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        }
    };

    const archiveCurrentEmployee = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/archive-current`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Employé archivé !');
                fetchSettings();
                fetchHistory();
                // Clear form
                setEmployeeName('');
                setEmployeePosition('');
                setEmployeeMessage('');
                setEmployeePhoto('');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de l\'archivage');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        }
        setArchiveDialogOpen(false);
    };

    const deleteHistoryEntry = async () => {
        if (!selectedHistoryId) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/api/motivation/history/${selectedHistoryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Entrée supprimée');
                fetchHistory();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Erreur lors de la suppression');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        }
        setDeleteDialogOpen(false);
        setSelectedHistoryId(null);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR').format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="p-6 animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                <div className="grid gap-6">
                    <div className="h-64 bg-gray-200 rounded"></div>
                    <div className="h-64 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto" data-testid="motivation-admin-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Sparkles className="w-6 h-6 text-amber-500" />
                            Gestion Motivation
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Objectif du jour et Employé du mois
                        </p>
                    </div>
                </div>
            </div>

            {/* Objectif du jour */}
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                        <Target className="w-5 h-5" />
                        Objectif du jour
                    </CardTitle>
                    <CardDescription>
                        Définissez l'objectif de ventes journalier (montant manuel, sans lien avec les ventes réelles)
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="objective-amount">Montant (FCFA)</Label>
                            <Input
                                id="objective-amount"
                                type="number"
                                value={objectiveAmount}
                                onChange={(e) => setObjectiveAmount(parseInt(e.target.value) || 0)}
                                placeholder="Ex: 500000"
                                className="text-xl font-bold"
                                data-testid="objective-amount-input"
                            />
                            <p className="text-sm text-muted-foreground">
                                Affiché : {formatCurrency(objectiveAmount)} FCFA
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="objective-message">Message de motivation</Label>
                            <Textarea
                                id="objective-message"
                                value={objectiveMessage}
                                onChange={(e) => setObjectiveMessage(e.target.value)}
                                placeholder="Ex: Ensemble, dépassons nos limites !"
                                rows={3}
                                data-testid="objective-message-input"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button 
                            onClick={saveObjective} 
                            disabled={saving}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="save-objective-btn"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? 'Enregistrement...' : 'Enregistrer l\'objectif'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Employé du mois */}
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-purple-700">
                        <Award className="w-5 h-5" />
                        Employé du mois
                    </CardTitle>
                    <CardDescription>
                        Mettez en avant un employé méritant
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Photo section */}
                        <div className="flex flex-col items-center gap-3">
                            <div className="relative">
                                <div className="w-32 h-32 rounded-full overflow-hidden bg-purple-100 ring-4 ring-purple-300 flex items-center justify-center">
                                    {employeePhoto ? (
                                        <img 
                                            src={employeePhoto} 
                                            alt="Employee"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-16 h-16 text-purple-300" />
                                    )}
                                </div>
                                {employeePhoto && (
                                    <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1.5">
                                        <Star className="w-4 h-4 text-yellow-800 fill-yellow-800" />
                                    </div>
                                )}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handlePhotoUpload}
                                accept="image/*"
                                className="hidden"
                            />
                            <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="upload-photo-btn"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {employeePhoto ? 'Changer' : 'Ajouter'} la photo
                            </Button>
                            <p className="text-xs text-muted-foreground">Max 2 Mo</p>
                        </div>

                        {/* Info section */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="employee-name">Nom complet</Label>
                                <Input
                                    id="employee-name"
                                    value={employeeName}
                                    onChange={(e) => setEmployeeName(e.target.value)}
                                    placeholder="Ex: Jean Dupont"
                                    data-testid="employee-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="employee-position">Poste</Label>
                                <Input
                                    id="employee-position"
                                    value={employeePosition}
                                    onChange={(e) => setEmployeePosition(e.target.value)}
                                    placeholder="Ex: Serveur"
                                    data-testid="employee-position-input"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="employee-message">Message de félicitations</Label>
                                <Textarea
                                    id="employee-message"
                                    value={employeeMessage}
                                    onChange={(e) => setEmployeeMessage(e.target.value)}
                                    placeholder="Ex: Bravo pour ton excellent travail ce mois-ci !"
                                    rows={2}
                                    data-testid="employee-message-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2 border-t">
                        {settings.employee_of_month?.name && (
                            <Button 
                                variant="outline"
                                onClick={() => setArchiveDialogOpen(true)}
                                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                            >
                                <Archive className="w-4 h-4 mr-2" />
                                Archiver l'actuel
                            </Button>
                        )}
                        <Button 
                            onClick={() => saveEmployee(false)} 
                            disabled={saving}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="save-employee-btn"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Historique */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                        <History className="w-5 h-5 text-gray-500" />
                        Historique des Employés du mois
                    </CardTitle>
                    <CardDescription>
                        Archive des anciens employés du mois
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {history.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                            <p>Aucun historique disponible</p>
                            <p className="text-sm">L'historique apparaîtra ici lorsque vous archiverez un employé du mois</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-16">Photo</TableHead>
                                        <TableHead>Nom</TableHead>
                                        <TableHead>Poste</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead className="w-20">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {history.map((entry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell>
                                                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100">
                                                    {entry.photo_url ? (
                                                        <img 
                                                            src={entry.photo_url} 
                                                            alt={entry.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <User className="w-5 h-5 text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium">{entry.name}</TableCell>
                                            <TableCell>{entry.position}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatDate(entry.archived_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => {
                                                        setSelectedHistoryId(entry.id);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                    data-testid={`delete-history-${entry.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Archive Dialog */}
            <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Archive className="w-5 h-5 text-purple-500" />
                            Archiver l'employé actuel ?
                        </DialogTitle>
                        <DialogDescription>
                            L'employé du mois actuel ({settings.employee_of_month?.name}) sera ajouté à l'historique.
                            Vous pourrez ensuite définir un nouvel employé du mois.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={archiveCurrentEmployee}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            <Archive className="w-4 h-4 mr-2" />
                            Archiver
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete History Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Supprimer cette entrée ?
                        </DialogTitle>
                        <DialogDescription>
                            Cette action est irréversible. L'entrée sera définitivement supprimée de l'historique.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={deleteHistoryEntry}
                            variant="destructive"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MotivationAdminPage;
